import { type FC, memo, useCallback, useRef, useState } from 'react';
import clsx from 'clsx';
import useOnclickOutside from 'react-cool-onclickoutside';
import { useFormContext } from 'react-hook-form';
import { ReactComponent as ArrowDown } from 'assets/images/ArrowDown.svg?react';
import { ReactComponent as ArrowUp } from 'assets/images/ArrowUp.svg?react';
import DropdownOptions from 'components/DropdownOptions';
import { type SelectOption } from 'components/DropdownOptions/DropdownOptions';
import RootInput from 'components/Input/RootInput/RootInput';
import useAccessibleDropdown from './hook';
import styles from './Select.module.scss';

interface SelectProps {
  name: string;
  label: string;
  options: SelectOption[];
  containerClassName?: string;
  searchable: boolean;
  maxLength?: number;
  required: boolean;
}

const ICON_KEY = 'select-input';

const Select: FC<SelectProps> = (props) => {
  const { name, label, options, containerClassName, searchable, maxLength, required } = props;

  const [selectFocused, setSelectFocused] = useState<boolean>(false);
  const [filteredOptions, setFilteredOptions] = useState<SelectOption[]>(options);

  const { getValues, setFocus, control, setValue, clearErrors, watch } = useFormContext();

  const selectedOptionValue = watch(name);

  const optionsRef = useRef<HTMLUListElement>(null);

  const dropdownListId = `${name}-select-list`;

  const handleOptionSelect = useCallback(
    (item: SelectOption) => {
      setValue(name, item.value);
      clearErrors(name);
    },
    [clearErrors, name, setValue],
  );

  const setMainInputFocus = useCallback(() => {
    setFocus(name, {});
  }, [name, setFocus]);

  const { dropdownOpened, toggleDropdown, optionsActive, clear } =
    useAccessibleDropdown<SelectOption>({
      options: filteredOptions,
      onChange: handleOptionSelect,
      optionsRef,
      isMainInputFocused: selectFocused,
      isMainInputTypingEnabled: false,
      setMainInputFocus,
    });

  const onClick = useCallback(
    (item: string) => {
      setValue(name, item);
      clearErrors(name);
      toggleDropdown(false);
    },
    [clearErrors, name, setValue, toggleDropdown],
  );

  const handleOnBlur = useCallback(() => {
    setSelectFocused(false);

    if (searchable && !dropdownOpened) {
      const matchingOption = options.find((option) => option.label === getValues(name));

      if (!matchingOption) {
        setValue(name, '');
        clearErrors(name);
      }
    }
  }, [clearErrors, dropdownOpened, getValues, name, options, searchable, setValue]);

  const handleOnFocus = useCallback(() => {
    setSelectFocused(true);
  }, []);

  const handleOnClick = useCallback(() => {
    toggleDropdown();
  }, [toggleDropdown]);

  const handleFiltering = useCallback(() => {
    const matchingOptions = options.filter((option) => option.label.includes(getValues(name)));

    setFilteredOptions(matchingOptions);

    if (!dropdownOpened) {
      toggleDropdown(true);
    }
  }, [dropdownOpened, getValues, name, options, toggleDropdown]);

  const handleOutsideClick = useCallback(() => {
    setSelectFocused(false);
    clear();
  }, [clear]);

  const outsideClickRef = useOnclickOutside(handleOutsideClick);

  const selectInputIcon = dropdownOpened ? (
    <ArrowUp key={ICON_KEY} role="none" />
  ) : (
    <ArrowDown key={ICON_KEY} role="none" />
  );

  return (
    <div className={clsx(styles.container, containerClassName)} ref={outsideClickRef}>
      <RootInput
        name={name}
        control={control}
        inputClassName={clsx(styles.selectInput, { [styles.notSearchable]: !searchable })}
        externalOnBlur={handleOnBlur}
        externalOnFocus={handleOnFocus}
        externalOnClick={handleOnClick}
        label={label}
        icon={selectInputIcon}
        readOnly={!searchable}
        clearErrors={clearErrors}
        {...(searchable && { externalOnChange: handleFiltering, maxLength })}
        showError={!dropdownOpened}
        required={required}
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={dropdownOpened}
        aria-controls={dropdownListId}
        aria-autocomplete="list"
        aria-activedescendant={
          selectedOptionValue ? `${dropdownListId}-${selectedOptionValue}` : undefined
        }
      />
      <DropdownOptions
        options={filteredOptions}
        onMouseUp={onClick}
        isOpen={dropdownOpened}
        optionsActive={optionsActive}
        ref={optionsRef}
        searchable={searchable}
        dropdownListId={dropdownListId}
        selectedOptionValue={selectedOptionValue}
      />
    </div>
  );
};

export default memo(Select);
